import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {Id, View} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, LabelMedium} from "@khanacademy/wonder-blocks-typography";

type Props = {
    // Called when the Cancel button is clicked
    onCancel?: () => unknown;
    // Called when the Save button is clicked
    onSaveAndClose: () => unknown;
    // The model this settings dialog is editing
    scratchpad: any;
};

type State = {
    newWidth: number | null | undefined;
    newHeight: number | null | undefined;
};

class EditControls extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            newWidth: null,
            newHeight: null,
        };
    }

    handleWidthChange: (width: string) => void = (width: string) => {
        this.setState({newWidth: parseInt(width, 10)});
    };

    handleHeightChange: (height: string) => void = (height: string) => {
        this.setState({newHeight: parseInt(height, 10)});
    };

    handleSave: () => void = () => {
        if (this.state.newWidth) {
            this.props.scratchpad.set("width", this.state.newWidth);
        }
        if (this.state.newHeight) {
            this.props.scratchpad.set("height", this.state.newHeight);
        }
        this.props.onSaveAndClose();
    };

    renderModal(idRoot: string): React.ReactNode {
        const scratchpad = this.props.scratchpad;

        const sizes = [
            ["400", t`400 (default)`],
            ["500", "500"],
            ["600", "600"],
        ];

        const sizeOptions = sizes.map((size, ind) => (
            <OptionItem key={ind} label={size[1]} value={size[0]} />
        ));

        const showWidth = scratchpad.get("userAuthoredContentType") === "pjs";
        const selectedWidth = this.state.newWidth || scratchpad.get("width");
        const selectedHeight = this.state.newHeight || scratchpad.get("height");

        const widthLabel = `${idRoot}width-label`;
        const widthSelect = `${idRoot}width-select`;
        const heightLabel = `${idRoot}height-label`;
        const heightSelect = `${idRoot}height-select`;
        return (
            <OnePaneDialog
                title={t`Settings`}
                content={
                    <View>
                        {showWidth && (
                            <React.Fragment>
                                <LabelMedium
                                    tag="label"
                                    id={widthLabel}
                                    htmlFor={widthSelect}
                                    style={styles.label}
                                >
                                    {t`Width`}
                                </LabelMedium>
                                <SingleSelect
                                    aria-labelledby={widthLabel}
                                    id={widthSelect}
                                    placeholder={t`Choose an option`}
                                    selectedValue={selectedWidth.toString()}
                                    onChange={this.handleWidthChange}
                                    style={styles.select}
                                >
                                    {sizeOptions}
                                </SingleSelect>
                            </React.Fragment>
                        )}
                        <LabelMedium
                            tag="label"
                            id={heightLabel}
                            htmlFor={heightSelect}
                            style={styles.label}
                        >
                            {t`Height`}
                        </LabelMedium>
                        <SingleSelect
                            aria-labelledby={heightLabel}
                            id={heightSelect}
                            placeholder={t`Choose an option`}
                            selectedValue={selectedHeight.toString()}
                            onChange={this.handleHeightChange}
                            style={styles.select}
                        >
                            {sizeOptions}
                        </SingleSelect>
                        <Body>
                            {t`To see the full effect of the size on your canvas, reload your program after saving.`}
                        </Body>
                    </View>
                }
                footer={
                    <View style={styles.modalFooter}>
                        <Button kind="tertiary" onClick={this.props.onCancel}>
                            {t`Cancel`}
                        </Button>
                        <Strut size={spacing.medium_16} />
                        <Button kind="primary" onClick={this.handleSave}>
                            {t`Save and Close`}
                        </Button>
                    </View>
                }
                style={styles.modal}
            />
        );
    }

    render(): React.ReactNode {
        return <Id>{(idRoot) => this.renderModal(idRoot)}</Id>;
    }
}

const styles = StyleSheet.create({
    label: {
        marginBottom: spacing.small_12,
    },
    select: {
        marginBottom: spacing.medium_16,
    },
    modal: {
        maxHeight: 420,
    },
    modalFooter: {
        flexDirection: "row",
        justifyContent: "flex-end",
    },
});

export default EditControls;
